import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, DialogActions } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useFormikContext } from 'formik';
import classNames from 'classnames';
import { LoadingButton } from '~/modules/common/components';
import { usePutAllocateResourceDialogSettings } from './hooks/useAllocateResourceDialogSettings';

const useStyles = makeStyles(theme => ({
  actionButton: {
    flexGrow: 0,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    transition: theme.transitions.create(['opacity'], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shortest
    }),
    marginRight: theme.spacing(1)
  },
  footer: {
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap'
  },
  settingsButton: {
    marginRight: 'auto'
  },
  settingsButtonFullWidth: {
    width: '100%'
  }
}));

const AllocateResourceDialogFooter = ({
  onClose,
  handleAddResourceAllocation,
  isSaving,
  isLoading
}) => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const {
    putAllocateResourceDialogSettings
  } = usePutAllocateResourceDialogSettings();

  const {
    totalAllocatedHours,
    load,
    user,
    project,
    showAdvancedSettings
  } = values;

  const onToggleSettings = useCallback(() => {
    const newValue = !showAdvancedSettings;

    setFieldValue('showAdvancedSettings', newValue);
    putAllocateResourceDialogSettings({ showAdvancedSettings: newValue });
  }, [setFieldValue, showAdvancedSettings, putAllocateResourceDialogSettings]);

  const onSubmit = useCallback(
    event => {
      handleAddResourceAllocation(values);
    },
    [handleAddResourceAllocation, values]
  );

  const isDisabled =
    isSaving ||
    isLoading ||
    !user ||
    !project ||
    (!showAdvancedSettings && (load && totalAllocatedHours) === 0);

  return (
    <DialogActions className={classes.footer}>
      <div
        className={classNames(classes.settingsButton, {
          [classes.settingsButtonFullWidth]: !showAdvancedSettings
        })}
      >
        <Button
          data-qe-id="AllocateResourceSettingsButton"
          onClick={onToggleSettings}
          variant="text"
          color="primary"
        >
          <FormattedMessage
            id={
              showAdvancedSettings
                ? 'allocateResourcesContent.basicSettings'
                : 'allocateResourcesContent.advancedSettings'
            }
          />
        </Button>
      </div>
      <Button
        data-qe-id="AllocateResourceCancelButton"
        onClick={onClose}
        variant="contained"
      >
        <FormattedMessage id="allocateResourcesContent.cancel" />
      </Button>
      <LoadingButton
        data-qe-id="AllocateResourceAddButton"
        className={classes.actionButton}
        color="primary"
        onClick={onSubmit}
        variant="contained"
        isLoading={isSaving}
        disabled={isDisabled}
      >
        <FormattedMessage id="allocateResourcesContent.allocateResource" />
      </LoadingButton>
    </DialogActions>
  );
};

AllocateResourceDialogFooter.propTypes = {
  onClose: PropTypes.func.isRequired,
  handleAddResourceAllocation: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  isLoading: PropTypes.bool
};

export default AllocateResourceDialogFooter;
